import { PRODUCT_TYPES } from '@/lib/constants';
import z from 'zod';

// Shared page/runtime schema source of truth
export enum PageStatus {
    DOES_NOT_EXIST = -1,
    DELETED = 0,
    PUBLISHED = 1,
    DRAFT = 2,
}

export const BUTTON_LAYOUTS = {
    BUTTON: 'button',
    CALLOUT: 'callout',
    PREVIEW: 'preview',
} as const;

export type ButtonLayout = (typeof BUTTON_LAYOUTS)[keyof typeof BUTTON_LAYOUTS];

export enum PaymentInterval {
    MONTHLY = 'month',
    YEARLY = 'year',
    WEEKLY = 'week',
    DAILY = 'day',
}

export enum BookingType {
    ONE_ON_ONE = 'one-on-one',
    GROUP = 'group',
    WEBINAR = 'webinar',
}

const DigitalAssetTypeSchema = z.union([z.enum(['file', 'link', 'url']), z.literal('')]);

const PageTypeSchema = z.enum(Object.values(PRODUCT_TYPES) as [string, ...string[]]);

export const PageButtonSchema = z.object({
    layout: z.nativeEnum(BUTTON_LAYOUTS),
    title: z.string().optional(),
    button_text: z.string().min(1),
    tagline: z.string(),
    image: z.string().url(),
    color: z.string().optional(),
    background_color: z.string().optional(),
    border_radius: z.number().optional(),
    font_size: z.number().optional(),
});

const PriceOptionsSchema = z.object({
    amount: z.number(),
    currency: z.string(),
    interval: z.nativeEnum(PaymentInterval).optional(),
    label: z.string().optional(),
    membership_id: z.number().optional(),
});

const PriceSchema = z.object({
    amount: z.number(),
    currency: z.string(),
    sale_amount: z.number().optional(),
    sale_amount_available: z.boolean().optional(),
    discount_codes_available: z.boolean().optional(),
    interval: z.nativeEnum(PaymentInterval).optional(),
    membership_id: z.number().optional(),
    membership_duration_available: z.boolean().optional(),
    membership_duration: z.number().optional(),
    stan_flexible_payment: z
        .object({
            enabled: z.boolean(),
            deposit_percentage: z.number().optional(),
            installments: z.number().optional(),
        })
        .optional(),
    price_options: z.record(z.string(), PriceOptionsSchema).optional(),
});

const QuestionOptionSchema = z.object({
    value: z.string(),
    id: z.string().optional(),
});

const QuestionSchema = z.object({
    required: z.boolean(),
    content: z.string(),
    type: z.enum(['name', 'email', 'phone', 'text', 'radio', 'dropdown', 'checkbox']),
    options: z.array(QuestionOptionSchema).optional(),
});

const ReviewSchema = z.object({
    id: z.string().optional(),
    name: z.string(),
    description: z.string(),
    rating: z.number(),
    image: z.string().optional(),
});

const InventorySchema = z.object({
    stock: z.number().optional(),
    manage: z.boolean().optional(),
    allow_backorders: z.boolean().optional(),
    low_stock_amount: z.number().optional(),
});

const MeetingReminderTimingSchema = z.object({
    offset: z.number(),
    unit: z.enum(['minutes', 'hours', 'days']),
});

const MeetingReminderSchema = z.object({
    email_reminder_enabled: z.boolean(),
    timings: z.array(MeetingReminderTimingSchema),
});

const MeetingDataSchema = z.object({
    meeting_id: z.number().optional(),
    description: z.string().optional(),
    duration_minutes: z.number(),
    price: PriceSchema.optional(),
    timezone: z.string(),
    slug: z.string().optional(),
    meeting_prep_hours: z.number().optional(),
    availability: z.record(z.coerce.number().min(1).max(7), z.array(z.array(z.string()))),
    questions: z.array(QuestionSchema).optional(),
    type: z.nativeEnum(BookingType),
    platform: z.string().optional(),
    attendees_limit: z.number(),
    before_buffer_time: z.number().optional(),
    after_buffer_time: z.number().optional(),
    book_within_the_next_n_days: z.number().optional(),
    reminders: MeetingReminderSchema.optional(),
});

const WebinarSessionSchema = z.object({
    date: z.string(),
    time: z.string(),
    duration_minutes: z.number().optional(),
});

const WebinarDataSchema = z.object({
    meeting_id: z.number().optional(),
    description: z.string().optional(),
    duration_minutes: z.number(),
    price: PriceSchema.optional(),
    timezone: z.string(),
    slug: z.string().optional(),
    sessions: z.array(WebinarSessionSchema),
    type: z.literal(BookingType.WEBINAR),
    platform: z.string().optional(),
    attendees_limit: z.number(),
    reminders: MeetingReminderSchema.optional(),
});

const DigitalAssetSchema = z.object({
    name: z.string(),
    url: z.string(),
    size: z.number().optional(),
    type: z.string().optional(),
    file_id: z.string().optional(),
});

const DigitalDownloadDataSchema = z.object({
    digital_assets_type: DigitalAssetTypeSchema,
    digital_assets: z.array(DigitalAssetSchema).optional(),
    title: z.string().optional(),
    description: z.string().optional(),
    price: PriceSchema.optional(),
});

const MarketingPageDataSchema = z.object({
    type: z.string(),
    html: z.string().optional(),
    grapesjs: z.record(z.any()).optional(),
});

const FulfillmentDataSchema = z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    price: PriceSchema.optional(),
});

const LeadMagnetDataSchema = z.object({
    digital_assets_type: DigitalAssetTypeSchema,
    digital_assets: z.array(DigitalAssetSchema).optional(),
    title: z.string().optional(),
    questions: z.array(QuestionSchema).optional(),
});

const CommunityIntegrationDataSchema = z.object({
    enabled: z.boolean(),
    community_id: z.number().optional(),
});

const MembershipDataSchema = z.object({
    stripe_product_id: z.string().optional(),
    access_duration: z.number().optional(),
    access_type: z.string().optional(),
    community_integration: CommunityIntegrationDataSchema.optional(),
    recurring_billing: z.boolean().optional(),
});

const LinkDataSchema = z.object({
    url: z.string(),
    open_in_new_tab: z.boolean().optional(),
});

const CourseThemeSchema = z.object({
    highlight_color: z.string().optional(),
    background_color: z.string().optional(),
    title_font: z.string().optional(),
});

const CourseDataInnerSchema = z.object({
    title: z.string(),
    description: z.string().optional(),
    banner_image_url: z.string().optional(),
    theme: CourseThemeSchema.optional(),
});

const LessonDataSchema = z.object({
    title: z.string(),
    description: z.string().optional(),
    video: z.string().optional(),
    digital_assets: z.array(DigitalAssetSchema).optional(),
    duration_minutes: z.number().optional(),
    preview: z.boolean().optional(),
});

const LessonSchema = z.object({
    lesson_id: z.number(),
    module_id: z.number(),
    status: z.number(),
    order: z.number(),
    data: LessonDataSchema,
});

const DripSettingsSchema = z.object({
    days: z.number(),
    enabled: z.boolean().optional(),
});

const ModuleDataSchema = z.object({
    title: z.string(),
    description: z.string().optional(),
    drip_settings: DripSettingsSchema.optional(),
});

const ModuleSchema = z.object({
    module_id: z.number(),
    status: z.number(),
    order: z.number(),
    data: ModuleDataSchema,
    lessons: z.array(LessonSchema),
});

const CourseTocItemSchema = z.object({
    title: z.string(),
    drip: z.boolean(),
});

const CourseTocSchema = z
    .object({
        toc: z.array(CourseTocItemSchema),
    })
    .strict();

const CourseModulesSchema = z
    .object({
        data: CourseDataInnerSchema,
        modules: z.array(ModuleSchema).optional(),
    })
    .strict();

const CourseDataSchema = z.union([CourseModulesSchema, CourseTocSchema]);

const AvatarVoiceSettingsSchema = z.object({
    voice_id: z.string().optional(),
    speed: z.number().optional(),
    pitch: z.number().optional(),
});

const AvatarDataInnerSchema = z.object({
    name: z.string().optional(),
    description: z.string().optional(),
    personality: z.string().optional(),
    avatar_image: z.string().optional(),
    voice_settings: AvatarVoiceSettingsSchema.optional(),
    training_data: z.string().optional(),
});

const AvatarDataSchema = z.object({
    data: AvatarDataInnerSchema,
});

const FunnelPageDataSchema = z.object({
    reference_page_id: z.number().optional(),
});

const MailchimpIntegrationSchema = z.object({
    list_id: z.string().optional(),
    api_key: z.string().optional(),
    enabled: z.boolean().optional(),
});

const ProductSchema = z
    .object({
        id: z.number().optional(),
        type: PageTypeSchema,
        title: z.string(),
        description: z.string(),
        image: z.string().optional(),
        slug: z.string().optional(),
        url: z.string().optional(),
        tag: z.string().optional(),
        price: PriceSchema.optional(),
        questions: z.array(QuestionSchema).optional(),
        reviews: z.array(ReviewSchema).optional(),
        inventory: InventorySchema.optional(),
        meeting: MeetingDataSchema.optional(),
        webinar: WebinarDataSchema.optional(),
        digital_download: DigitalDownloadDataSchema.optional(),
        marketing_page: MarketingPageDataSchema.optional(),
        fulfillment: FulfillmentDataSchema.optional(),
        lead_magnet: LeadMagnetDataSchema.optional(),
        membership: MembershipDataSchema.optional(),
        ai_avatar: AvatarDataSchema.optional(),
        link: LinkDataSchema.optional(),
        course: CourseDataSchema.optional(),
        funnel_page: FunnelPageDataSchema.optional(),
        emails: z.record(z.string(), z.any()).optional(),
        mailchimp_integration: MailchimpIntegrationSchema.optional(),
        payment_methods: z.record(z.string(), z.boolean()).optional(),
    })
    .passthrough();

const SecondBuiltInProductSchema = z.object({
    enabled: z.boolean(),
    page_id: z.number(),
});

const AffiliateShareSchema = z.object({
    percent: z.number().int().min(1).max(90),
    status: z.enum(['new', 'active', 'deleted']),
});

const AffiliateTermsSchema = z.object({
    enabled: z.boolean(),
    text: z.string(),
    agreed_at: z.string().optional(),
});

const ResellRightsSchema = z.object({
    enabled: z.boolean().optional(),
    percentage: z.number().optional(),
    affiliate_terms_and_condition: AffiliateTermsSchema.optional(),
});

const CommunityIntegrationSchema = z.object({
    enabled: z.boolean(),
});

const CountdownTimerSchema = z.object({
    end_date: z.string().optional(),
    show_timer: z.boolean().optional(),
    timezone: z.string().optional(),
    expired_message: z.string().optional(),
});

const CurrencyOverrideSchema = z.object({
    currency: z.string().optional(),
    enabled: z.boolean().optional(),
});

export const PageDataSchema = z
    .object({
        location: z.string(),
        primary_color: z.string().optional(),
        secondary_color: z.string().optional(),
        bottom_title: z.string().optional(),
        video: z.string().optional(),
        top_banner_text: z.string().nullable().optional(),
        product: ProductSchema,
        button: PageButtonSchema.optional(),
        submit_title: z.string().optional(),
        second_built_in_product: SecondBuiltInProductSchema.optional(),
        community_integration: CommunityIntegrationSchema.optional(),
        affiliate_shares: z
            .array(AffiliateShareSchema)
            .superRefine((items, ctx) => {
                const uniquePercentages = new Set(items.map(item => item.percent));
                if (uniquePercentages.size !== items.length) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Affiliate share percentages must be unique',
                    });
                }
            })
            .optional(),
        resell_rights: ResellRightsSchema.optional(),
        countdown_timer: CountdownTimerSchema.optional(),
        currency_override: CurrencyOverrideSchema.optional(),
        product_generation_ulid: z.string().optional(),
        funnel_page_id: z.number().optional(),
    })
    .passthrough();

export const PageSchema = z.object({
    page_id: z.number(),
    store_id: z.number(),
    type: PageTypeSchema,
    status: z.nativeEnum(PageStatus),
    slug: z.string().nullable().optional(),
    order: z.number(),
    data: PageDataSchema,
    created_at: z.number().optional(),
    updated_at: z.number().optional(),
    url: z.string().optional(),
    storeName: z.string().optional(),
});

export type PageData = z.infer<typeof PageDataSchema>;
export type Page = z.infer<typeof PageSchema>;
export type Product = z.infer<typeof ProductSchema>;
